import { Box, Drawer, useMediaQuery } from "@mui/material";
// material-ui
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";

import { DRAWER_WIDTH } from "@/config";
// utils
import { getTestId } from "@/utils/test-utils";

import DrawerContent from "./DrawerContent";
// project import
import DrawerHeader from "./DrawerHeader";
import MiniDrawerStyled from "./MiniDrawerStyled";

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

interface MainDrawerProps {
  open: boolean;
  handleDrawerToggle: () => void;
  window?: () => Window;
}

const MainDrawer = ({ open, handleDrawerToggle, window }: MainDrawerProps) => {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent {...getTestId("drawer-content")} />, []);
  const drawerHeader = useMemo(
    () => (
      <DrawerHeader
        {...getTestId("drawer-header")}
        open={open}
        handleDrawerToggle={handleDrawerToggle}
      />
    ),
    [open, handleDrawerToggle]
  );

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, zIndex: 1300 }} aria-label="mailbox folders">
      {!matchDownLG ? (
        <MiniDrawerStyled {...getTestId("mini-drawer")} variant="permanent" open={open}>
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          {...getTestId("navigation-drawer")}
          container={container}
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
            style: { pointerEvents: open ? "auto" : "none" },
          }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundImage: "none",
              boxShadow: "inherit",
              background: theme.palette.background.default,
            },
          }}
        >
          {open && drawerHeader}
          {open && drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default MainDrawer;
